import { useCallback, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "bharatMail.notes";

function hydrateNotes(raw) {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((note) => note && typeof note === "object" && note.id != null)
      .map((note) => ({
        id: note.id,
        title: String(note.title || "Untitled"),
        body: String(note.body || ""),
        createdAt: note.createdAt || new Date().toISOString(),
        updatedAt: note.updatedAt || note.createdAt || new Date().toISOString(),
      }));
  } catch {
    return [];
  }
}

export default function useNotes() {
  const [notes, setNotes] = useState(() => {
    if (typeof window === "undefined") return [];
    return hydrateNotes(localStorage.getItem(STORAGE_KEY));
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(notes));
    } catch {}
  }, [notes]);

  const addNote = useCallback((payload) => {
    let created = null;
    setNotes((prev) => {
      const now = new Date().toISOString();
      const id = Date.now();
      const title = String(payload?.title || "Untitled");
      const body = String(payload?.body || "");
      created = {
        id,
        title,
        body,
        createdAt: now,
        updatedAt: now,
      };
      return [created, ...prev];
    });
    return created;
  }, []);

  const updateNote = useCallback((id, payload) => {
    if (id == null) return;
    setNotes((prev) =>
      prev.map((note) => {
        if (note.id !== id) return note;
        const now = new Date().toISOString();
        return {
          ...note,
          title: payload?.title != null ? String(payload.title || "Untitled") : note.title,
          body: payload?.body != null ? String(payload.body || "") : note.body,
          updatedAt: now,
        };
      })
    );
  }, []);

  const deleteNote = useCallback((id) => {
    if (id == null) return;
    setNotes((prev) => prev.filter((note) => note.id !== id));
  }, []);

  const getNoteById = useCallback((id) => notes.find((note) => note.id === id) || null, [notes]);

  const sortedNotes = useMemo(() => {
    return [...notes].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }, [notes]);

  return {
    notes: sortedNotes,
    addNote,
    updateNote,
    deleteNote,
    getNoteById,
  };
}
